// Batching decorator over the `SnapshotCatchupFetcher` seam (BL-1465).
//
// THE PROBLEM. `SnapshotStore` opens one channel AND one catch-up HTTP request
// per topic, with no batching. Since BL-1355 moved the bidder grid surfaces onto
// per-lot `lot:{id}:snapshot`, a grid page holds ~49 topics where it held 2 — so
// one browser loading one auction page fires 48 `lot_snapshot` catch-up GETs at
// once, and `reconcileAll()` re-fires all of them on every focus /
// visibilitychange / online / reconnect / 2-minute tick. Measured on dev (trace
// 09f17835a2139a073ed8b8edfa4d7b35): the burst serializes behind the connection
// pool at an 876ms tail, for ~24ms of real DB work per request.
//
// WHY THE FETCHER SEAM, not the store. `runCatchup` sets `entry.fetching = true`
// SYNCHRONOUSLY before awaiting the fetcher, and `reconcileAll` loops
// synchronously — so an entire mount burst (and an entire reconcile sweep) issues
// its fetcher calls inside ONE task. A decorator that coalesces on the microtask
// boundary therefore catches all of them without the store knowing anything
// changed. That leaves the store's per-topic state machine — the fetching flag,
// the hint buffer, the version cursor, the error classification — completely
// untouched, and every consumer hook unchanged.
//
// The coalescing window's actual tolerance (mutation-verified, see the
// store-driven tests): a MICROTASK hop between `subscribe()` and the fetcher call
// is fine — it still drains inside the same flush window. A MACROTASK hop
// (`setTimeout`, an I/O await) is not: it splits the burst and every lot goes out
// as its own request again, restoring the original bug silently. If you ever need
// to add an await inside `runCatchup` ahead of the fetcher call, keep it a
// microtask.
//
// SEMANTICS ARE PRESERVED EXACTLY. The store distinguishes three outcomes, and
// this decorator reproduces all three:
//
//   • resolve(result)  → snapshot applied
//   • resolve(null)    → 304, cursor matched, no-op
//   • reject(err)      → with a numeric `status`, the store RECORDS an error and
//                        renders "Try again"; WITHOUT one (a network reject) it
//                        stays loading and self-heals on the next reconcile.
//
// So a whole-request HTTP failure rejects every pending call WITH its status, a
// network reject rejects them WITHOUT one, and per-topic 403/404 reject only
// their own call — one unknown lot id can't blank its 47 healthy neighbours.
//
// 401/418 STAY AT THE HTTP LEVEL. `applyResponseStatusHandlers` dispatches on
// `Response.status`, so the batch endpoint returns whole-request auth failures as
// the HTTP status rather than as per-topic results. That keeps BUG-1015's
// banned-customer / stale-session routing working with no change to it.
//
// ⚠️ THE INVARIANT THIS FILE EXISTS TO UPHOLD: **every queued promise must
// settle.** A promise left hanging leaves `entry.fetching = true` forever, and
// `reconcileAll()` skips fetching topics — so that lot would wedge permanently,
// never retrying, with no error shown. Hence the catch-all in `postBatch` and the
// explicit reject for a topic the server omitted from `results`.

import {
    applyResponseStatusHandlers,
    type ResponseStatusHandlers,
} from '../../api/response-status-handlers';

import { parseSnapshotTopic } from './parse-snapshot-topic';
import {
    SnapshotCatchupError,
    type SnapshotCatchupFetcher,
    type SnapshotCatchupResult,
} from './snapshot-store';

/**
 * Default ceiling on topics per batched request. Mirrors the server factory's
 * `DEFAULT_MAX_BATCH_SIZE`; groups larger than this are split across several
 * requests rather than rejected.
 */
export const DEFAULT_CLIENT_MAX_BATCH_SIZE = 100;

/**
 * One queued catch-up call awaiting the next flush. It carries no
 * `parseSnapshotTopic` `params` (BL-2491's `tenant_id` for the staff count), so a
 * kind that needs them must stay off the batchable list and take the
 * single-topic fallback, which sends them.
 */
interface PendingCall {
    /** The original topic string — needed to delegate to `fallback`. */
    topic: string;
    topicId: string;
    sinceVersion: number;
    resolve: (result: SnapshotCatchupResult | null) => void;
    reject: (err: unknown) => void;
}

/** One topic's outcome as it arrives in the batch response body. */
interface BatchResultEntry {
    topicId: string;
    status: number;
    snapshot?: unknown;
    version?: number;
    error?: string;
}

export interface BatchingSnapshotCatchupFetcherOpts {
    /**
     * The per-topic fetcher to fall back to — normally
     * `defaultSnapshotCatchupFetcher(...)`. Used verbatim for any kind not in
     * `batchableKinds`, and for a group that turns out to hold a single topic
     * (so a lot-detail page keeps today's exact single-topic GET behaviour).
     */
    fallback: SnapshotCatchupFetcher;
    /**
     * Snapshot kinds whose app exposes a `/<kind>/catchup/batch` endpoint.
     * Anything not listed goes straight to `fallback`, so adopting this is a
     * per-kind opt-in and an app with no batch routes behaves exactly as before.
     */
    batchableKinds: ReadonlySet<string>;
    /** Path prefix for catch-up routes. Defaults to `/api/realtime`. */
    catchupBase?: string;
    /** App-injected 401/418 handlers, applied to whole-request failures. */
    handlers?: ResponseStatusHandlers;
    /** Override the per-request topic ceiling. Defaults to {@link DEFAULT_CLIENT_MAX_BATCH_SIZE}. */
    maxBatchSize?: number;
    /**
     * Test seam for the coalescing boundary. Defaults to `queueMicrotask`, which
     * captures a whole React commit's worth of `subscribe()` calls (and a whole
     * `reconcileAll()` sweep) while adding zero latency. Tests inject a manual
     * scheduler to drive flushes deterministically.
     */
    scheduleFlush?: (flush: () => void) => void;
}

/**
 * Wrap a `SnapshotCatchupFetcher` so same-kind calls issued in one task collapse
 * into a single batched POST.
 *
 * Drop-in: the returned function has the identical `(topic, sinceVersion)`
 * signature and identical resolve/reject semantics, so `SnapshotStore` and every
 * consumer hook are unchanged.
 *
 * @param opts - see {@link BatchingSnapshotCatchupFetcherOpts}.
 * @returns a fetcher that batches where it can and delegates where it can't.
 * @sideEffect Issues one POST per batched group per flush (or delegates to
 *   `fallback`, which issues a GET). May invoke the injected 401/418 handlers,
 *   which typically navigate the browser.
 */
export function createBatchingSnapshotCatchupFetcher(
    opts: BatchingSnapshotCatchupFetcherOpts,
): SnapshotCatchupFetcher {
    const {
        fallback,
        batchableKinds,
        catchupBase = '/api/realtime',
        handlers,
        maxBatchSize = DEFAULT_CLIENT_MAX_BATCH_SIZE,
        scheduleFlush = (flush) => queueMicrotask(flush),
    } = opts;

    /** Calls queued since the last flush, grouped by snapshot kind. */
    const pendingByKind = new Map<string, PendingCall[]>();
    let flushScheduled = false;

    function flush(): void {
        flushScheduled = false;
        // Snapshot and clear FIRST: a fetcher call made while we're dispatching
        // must queue for the NEXT flush, not mutate the group we're walking.
        const groups = [...pendingByKind.entries()];
        pendingByKind.clear();

        for (const [kind, calls] of groups) {
            // A lone topic goes down the untouched single-topic path — a
            // lot-detail page shouldn't pay a new endpoint for one lot, and this
            // keeps the singleton case byte-identical to pre-BL-1465 behaviour.
            if (calls.length === 1) {
                const call = calls[0];
                fallback(call.topic, call.sinceVersion).then(call.resolve, call.reject);
                continue;
            }
            for (let i = 0; i < calls.length; i += maxBatchSize) {
                void postBatch(kind, calls.slice(i, i + maxBatchSize));
            }
        }
    }

    async function postBatch(kind: string, calls: PendingCall[]): Promise<void> {
        try {
            const url = new URL(
                `${catchupBase}/${kind}/catchup/batch`,
                // Browsers always have window.location; SSR callers pass an
                // absolute catchupBase to skip this.
                typeof window !== 'undefined'
                    ? window.location.origin
                    : 'http://localhost',
            );

            let res: Response;
            try {
                res = await fetch(url.toString(), {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    // Snapshot data is per-user / per-resource; never cache.
                    cache: 'no-store',
                    credentials: 'same-origin',
                    body: JSON.stringify({
                        topics: calls.map(({ topicId, sinceVersion }) => ({
                            topicId,
                            sinceVersion,
                        })),
                    }),
                });
            } catch (err) {
                // Network reject (offline, DNS). Carries NO status on purpose —
                // the store keeps these topics loading and retries on the next
                // `online` / reconcile, rather than showing a "Try again" the
                // user can't act on.
                rejectAll(calls, err);
                return;
            }

            if (!res.ok) {
                // Whole-request failure. Route 401/418 through the SAME app
                // handlers the single-topic fetcher uses, BEFORE rejecting.
                if (handlers) applyResponseStatusHandlers(res, handlers);
                rejectAll(
                    calls,
                    new SnapshotCatchupError(
                        res.status,
                        `snapshot batch catch-up failed: ${res.status} ${res.statusText}`,
                    ),
                );
                return;
            }

            let body: {
                success?: boolean;
                data?: { results?: BatchResultEntry[] };
            };
            try {
                body = (await res.json()) as typeof body;
            } catch (parseErr) {
                // Same reasoning as the malformed-envelope branch below: `res.ok`
                // was true, so an unparseable body (an HTML error page from a
                // proxy, a truncated response) is a server-side fault that will
                // recur on every retry — it must surface, not masquerade as a
                // transient network reject. Without this the parse throw would
                // reach the catch-all at the bottom and reject statuslessly.
                rejectAll(
                    calls,
                    new SnapshotCatchupError(
                        500,
                        `snapshot batch catch-up returned an unparseable body: ${
                            parseErr instanceof Error ? parseErr.message : String(parseErr)
                        }`,
                    ),
                );
                return;
            }
            const results = body?.data?.results;
            if (!body?.success || !Array.isArray(results)) {
                // Carries a status ON PURPOSE. `res.ok` was true, so the server
                // answered and the body is simply malformed — a serialization or
                // contract bug, NOT a network hiccup. A statusless reject would be
                // classified as "no response yet" and leave every topic in the
                // batch silently loading, retrying the same broken response on
                // every reconcile and never showing "Try again" (the BUG-1015
                // failure class). Matches `settleOne`'s omitted-topic branch.
                rejectAll(
                    calls,
                    new SnapshotCatchupError(
                        500,
                        `snapshot batch catch-up envelope missing results: ${JSON.stringify(body)}`,
                    ),
                );
                return;
            }

            const byTopicId = new Map(results.map((r) => [r.topicId, r]));
            for (const call of calls) {
                settleOne(call, byTopicId.get(call.topicId));
            }
        } catch (err) {
            // Catch-all backstop for the invariant in this file's header: no
            // queued promise may be left unsettled, or its topic wedges with
            // `fetching` stuck true. Re-settling an already-settled promise is a
            // no-op, so this is safe to run unconditionally.
            rejectAll(calls, err);
        }
    }

    return function batchingCatchupFetcher(topic, sinceVersion) {
        const parsed = parseSnapshotTopic(topic);
        if (!parsed || !batchableKinds.has(parsed.kind)) {
            return fallback(topic, sinceVersion);
        }
        return new Promise<SnapshotCatchupResult | null>((resolve, reject) => {
            const group = pendingByKind.get(parsed.kind);
            const call: PendingCall = {
                topic,
                topicId: parsed.topicId,
                sinceVersion,
                resolve,
                reject,
            };
            if (group) {
                group.push(call);
            } else {
                pendingByKind.set(parsed.kind, [call]);
            }
            if (!flushScheduled) {
                flushScheduled = true;
                scheduleFlush(flush);
            }
        });
    };
}

/**
 * Settle one queued call from its per-topic result. A topic the server omitted
 * from `results` is REJECTED, never left pending — see the header invariant.
 */
function settleOne(call: PendingCall, result: BatchResultEntry | undefined): void {
    if (!result) {
        call.reject(
            new SnapshotCatchupError(
                500,
                `snapshot batch catch-up omitted topic ${call.topicId}`,
            ),
        );
        return;
    }
    if (result.status === 304) {
        call.resolve(null);
        return;
    }
    if (result.status === 200) {
        call.resolve({
            snapshot: result.snapshot,
            version: result.version as number,
        });
        return;
    }
    // Per-topic failure (403 / 404). Scoped to this call alone so its neighbours
    // in the same batch still resolve normally.
    call.reject(
        new SnapshotCatchupError(
            result.status,
            result.error ?? `snapshot catch-up failed: ${result.status}`,
        ),
    );
}

/** Reject every queued call with the same error. Already-settled calls are no-ops. */
function rejectAll(calls: PendingCall[], err: unknown): void {
    for (const call of calls) call.reject(err);
}
